import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  Grid, 
  CircularProgress, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  BarChart as BarChartIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  AddShoppingCart as AddShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ArrowUpward as ArrowUpwardIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import ProductList from '../components/inventory/ProductList';
import WarehouseImportExport from '../components/inventory/WarehouseImportExport';
import WarehouseLoadForm from '../components/warehouse/WarehouseLoadForm';
import InventoryCountDialog from '../components/warehouse/InventoryCountDialog';
import api from '../utils/api';

// Importiamo un componente per i grafici
const BarChart = ({ data, title, valueKey, labelKey, color }) => {
  const maxValue = Math.max(...data.map(item => item[valueKey])) * 1.1;
  
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Box sx={{ mt: 1 }}>
        {data.map((item, index) => (
          <Box key={index} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ 
                maxWidth: '70%', 
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {item[labelKey] && item[labelKey].length > 80 
                  ? `${item[labelKey].substring(0, 80)}...` 
                  : item[labelKey]}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {typeof item[valueKey] === 'number' 
                  ? valueKey.toLowerCase().includes('price') 
                    ? `€${item[valueKey].toFixed(2)}`
                    : valueKey.toLowerCase().includes('margin')
                      ? `${item[valueKey].toFixed(2)}%`
                      : item[valueKey].toFixed(2)
                  : item[valueKey]}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(item[valueKey] / maxValue) * 100}
              sx={{ 
                height: 10, 
                borderRadius: 1,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: color
                }
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const Inventory = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalValue: 0,
    totalCost: 0,
    potentialProfit: 0,
    lowStockCount: 0,
    totalProducts: 0,
    categories: [],
    lowStockProducts: [],
    topSellingPrice: [],
    topPurchasePrice: [],
    bestMargin: [],
    topSoldProducts: [],
    topMaintProducts: []
  });
  const [openLowStockDialog, setOpenLowStockDialog] = useState(false);
  const [openStatsDialog, setOpenStatsDialog] = useState(false);
  const [openLoadForm, setOpenLoadForm] = useState(false);
  const [openInventoryDialog, setOpenInventoryDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [productListRefreshKey, setProductListRefreshKey] = useState(0);

  // Fetch inventory statistics
  useEffect(() => {
    fetchInventoryStats();
  }, []);

    const fetchInventoryStats = async () => {
      setLoading(true);
      try {
      // Fetch products
        const res = await api.get('/products', {
          params: { all: 'true' } // Richiedi tutti i prodotti per le statistiche
        });
      
      // Con all=true, la risposta è direttamente un array
      let products = [];
      if (Array.isArray(res.data)) {
        products = res.data;
      } else if (res.data && res.data.products) {
        products = res.data.products;
      } else {
        console.error('Formato risposta API prodotti non valido:', res.data);
        products = [];
      }

      // Dati di vendita e manutenzione - gestiti con try/catch separati
      let soldProducts = [];
      let maintProducts = [];

      try {
        // Fetch sales stats - gestione con try/catch separato
        const salesRes = await api.get('/sales/stats');
        if (salesRes.data && salesRes.data.topProducts) {
          soldProducts = salesRes.data.topProducts.map(p => ({
            name: p.productName || p.name,
            code: p.productCode || p.code,
            count: p.count || 0,
            totalValue: p.totalValue || 0
          }));
        }
      } catch (salesErr) {
        console.log('API statistiche vendite non disponibile:', salesErr.message);
        soldProducts = [];
      }

      try {
        // Fetch maintenance stats - gestione con try/catch separato
        const maintRes = await api.get('/maintenance/stats');
        if (maintRes.data && maintRes.data.topProducts) {
          maintProducts = maintRes.data.topProducts.map(p => ({
            name: p.productName || p.name,
            code: p.productCode || p.code,
            count: p.count || 0,
            totalValue: p.totalValue || 0
          }));
        }
      } catch (maintErr) {
        console.log('API statistiche manutenzioni non disponibile:', maintErr.message);
        maintProducts = [];
      }
      
      // Calculate total inventory value (selling price)
        const totalValue = products.reduce((sum, product) => {
          if (!product.isLabor) {
          return sum + (parseFloat(product.sellingPrice || 0) * (product.stock || 0));
        }
        return sum;
      }, 0);
      
      // Calculate total inventory cost (purchase price)
      const totalCost = products.reduce((sum, product) => {
        if (!product.isLabor) {
          return sum + (parseFloat(product.purchasePrice || 0) * (product.stock || 0));
          }
          return sum;
        }, 0);
      
      // Calculate potential profit
      const potentialProfit = totalValue - totalCost;
        
        // Count products with low stock (escludi quelli segnati "da non riassortire")
      const lowStockProducts = products.filter(product => 
          !product.isLabor && product.stock < product.minStock && !product.doNotRestock
      );
      
      // Count total products (excluding labor)
      const totalProducts = products.filter(product => !product.isLabor).length;
      
      // Group products by category
      const categoryCounts = {};
      products.forEach(product => {
        if (!product.isLabor) {
          const category = product.category || 'Non categorizzato';
          if (!categoryCounts[category]) {
            categoryCounts[category] = {
              count: 0,
              value: 0,
              cost: 0
            };
          }
          categoryCounts[category].count += 1;
          categoryCounts[category].value += parseFloat(product.sellingPrice || 0) * (product.stock || 0);
          categoryCounts[category].cost += parseFloat(product.purchasePrice || 0) * (product.stock || 0);
        }
      });
      
      const categories = Object.keys(categoryCounts).map(category => {
        const value = categoryCounts[category].value || 0;
        const cost = categoryCounts[category].cost || 0;
        return {
          name: category,
          count: categoryCounts[category].count,
          value: value,
          cost: cost,
          profit: value - cost,
          margin: value > 0 ? ((value - cost) / value) * 100 : 0
        };
      });
      
      // Top products by selling price
      const topSellingPrice = [...products]
        .filter(p => !p.isLabor && parseFloat(p.sellingPrice || 0) > 0)
        .sort((a, b) => parseFloat(b.sellingPrice || 0) - parseFloat(a.sellingPrice || 0))
        .slice(0, 5)
        .map(p => ({
          name: p.name || 'Prodotto senza nome',
          code: p.code || '',
          sellingPrice: parseFloat(p.sellingPrice || 0)
        }));
      
      // Top products by purchase price
      const topPurchasePrice = [...products]
        .filter(p => !p.isLabor && parseFloat(p.purchasePrice || 0) > 0)
        .sort((a, b) => parseFloat(b.purchasePrice || 0) - parseFloat(a.purchasePrice || 0))
        .slice(0, 5)
        .map(p => ({
          name: p.name || 'Prodotto senza nome',
          code: p.code || '',
          purchasePrice: parseFloat(p.purchasePrice || 0)
        }));
      
      // Products with best profit margin
      const bestMargin = [...products]
        .filter(p => !p.isLabor && parseFloat(p.purchasePrice || 0) > 0 && parseFloat(p.sellingPrice || 0) > 0)
        .map(p => {
          const sellingPrice = parseFloat(p.sellingPrice || 0);
          const purchasePrice = parseFloat(p.purchasePrice || 0);
          return {
            name: p.name || 'Prodotto senza nome',
            code: p.code || '',
            margin: sellingPrice > 0 ? ((sellingPrice - purchasePrice) / sellingPrice) * 100 : 0
          };
        })
        .filter(p => p.margin > 0)
        .sort((a, b) => b.margin - a.margin)
        .slice(0, 5);
      
      // Set all stats
      setStats({ 
        totalValue, 
        totalCost,
        potentialProfit,
        lowStockCount: lowStockProducts.length,
        lowStockProducts,
        totalProducts,
        categories,
        topSellingPrice,
        topPurchasePrice,
        bestMargin,
        topSoldProducts: soldProducts.slice(0, 5),
        topMaintProducts: maintProducts.slice(0, 5)
      });
    } catch (err) {
      console.error('Error fetching inventory stats:', err);
      // In caso di errore, impostiamo uno stato minimo con prodotti in esaurimento vuoti
      setStats({
        ...stats,
        lowStockCount: 0,
        lowStockProducts: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Funzione per normalizzare le immagini dei prodotti
  const getProductImage = (image) => {
    try {
      // Log per debug
      console.log('getProductImage - input originale:', image);
      
      if (!image) {
        console.log('getProductImage - immagine nulla o undefined, ritorno placeholder');
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
      }

      // Se l'immagine è un oggetto JSON, lo convertiamo in stringa
      let imageUrl = image;
      if (typeof imageUrl === 'object') {
        console.log('getProductImage - immagine è un oggetto:', imageUrl);
        imageUrl = imageUrl.text || imageUrl.hyperlink || imageUrl.value || '';
        console.log('getProductImage - dopo estrazione da oggetto:', imageUrl);
      }

      // Assicurati che l'URL sia una stringa
      imageUrl = String(imageUrl || '').trim();
      console.log('getProductImage - dopo conversione a stringa:', imageUrl);

      // Se l'immagine è vuota dopo la conversione
      if (!imageUrl) {
        console.log('getProductImage - URL vuoto dopo normalizzazione, ritorno placeholder');
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
      }

      // Rimuovi eventuali spazi o caratteri non validi nell'URL
      imageUrl = imageUrl.replace(/\s+/g, '');

      // Gestione URL completi
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        console.log('getProductImage - URL web completo:', imageUrl);
        return imageUrl;
      }
      
      // Gestione Base64
      if (imageUrl.startsWith('data:')) {
        console.log('getProductImage - immagine Base64');
        return imageUrl;
      }

      // Gestione percorsi locali
      if (imageUrl.startsWith('/uploads')) {
        const fullUrl = `${window.location.origin}${imageUrl}`;
        console.log('getProductImage - percorso uploads con origine:', fullUrl);
        return fullUrl;
      }

      // Gestione percorsi relativi
      const fullUrl = `${window.location.origin}/uploads/products/${imageUrl}`;
      console.log('getProductImage - percorso relativo con origine completa:', fullUrl);
      return fullUrl;
    } catch (err) {
      console.error('Errore nel caricamento dell\'immagine:', err);
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
    }
  };

  const handleAddToCart = async (product) => {
    try {
      setLoading(true);
      console.log('Richiesta aggiunta al carrello per prodotto:', product);
      
      // Verifica disponibilità nello shop
      const res = await api.get(`/b2b-products`, {
        params: {
          searchCode: product.code, // Usiamo searchCode anziché code
          limit: 100 // Aumentiamo il limite per trovare più corrispondenze possibili
        }
      });
      
      const shopProducts = res.data && res.data.products ? res.data.products : [];
      console.log('Prodotti trovati nel B2B:', shopProducts.length);
      
      if (shopProducts.length === 0) {
        // Prodotto non trovato nello shop
        setSnackbar({
          open: true,
          message: `Prodotto "${product.name}" non disponibile nello shop.`,
          severity: 'warning'
        });
        return;
      }
      
      // Cerca una corrispondenza esatta per il codice prodotto
      let shopProduct = null;
      const exactMatch = shopProducts.find(p => 
        p.code === product.code || 
        (p.id && p.id === product.id)
      );
      
      if (exactMatch) {
        shopProduct = exactMatch;
        console.log('Trovata corrispondenza esatta nel catalogo B2B:', shopProduct);
      } else {
        // Se non troviamo una corrispondenza esatta, usiamo il primo risultato
        shopProduct = shopProducts[0];
        console.log('Corrispondenza esatta non trovata, usando il primo risultato:', shopProduct);
      }
      
      if (!shopProduct.available || shopProduct.available <= 0) {
        // Prodotto esaurito nello shop
        setSnackbar({
          open: true,
          message: `Prodotto "${product.name}" non disponibile in magazzino.`,
          severity: 'warning'
        });
        return;
      }
      
      // Verifica se c'è un negozio associato e ottieni i dettagli del prodotto dal negozio
      let cart = [];
      try {
        const savedCart = localStorage.getItem(`shopCart_${api.defaults.headers['x-company-id']}`);
        if (savedCart) {
          cart = JSON.parse(savedCart);
        }
      } catch (err) {
        console.error('Errore nel caricamento del carrello:', err);
      }
      
      // Verifica se il prodotto è già nel carrello
      const existingIndex = cart.findIndex(item => item.id === shopProduct.id);
      
      // Determina la quantità minima corretta
      const minQty = shopProduct.minQty || 1;
      
      if (existingIndex >= 0) {
        // Incrementa la quantità se già presente
        cart[existingIndex].quantity += parseInt(minQty);
      } else {
        // Log dettagliato dell'immagine prima della preparazione
        console.log('Immagine prodotto prima di getProductImage:', shopProduct.image);
        
        // Preparazione dell'immagine utilizzando la funzione getProductImage
        const imageUrl = getProductImage(shopProduct.image);
        console.log('Immagine prodotto dopo getProductImage:', imageUrl);
        
        // Aggiungi nuovo prodotto al carrello con tutti i dati corretti
        cart.push({
          id: shopProduct.id,
          code: shopProduct.code,
          name: shopProduct.name || shopProduct.description,
          description: shopProduct.description || shopProduct.name,
          price: shopProduct.purchasePrice !== undefined && shopProduct.purchasePrice !== null ? 
                 parseFloat(shopProduct.purchasePrice) : 0,
          suggestedPrice: shopProduct.sellingPrice,
          quantity: parseInt(minQty),
          minQty: minQty,
          available: shopProduct.available,
          image: imageUrl,
          companyId: api.defaults.headers['x-company-id'] || localStorage.getItem('companyId')
        });
      }
      
      // Salva il carrello aggiornato
      const companyId = api.defaults.headers['x-company-id'] || localStorage.getItem('companyId');
      console.log('🛒 Salvando carrello con ID azienda:', companyId);
      
      // Verifica che ci sia un ID azienda valido
      if (!companyId) {
        console.error('Errore: impossibile salvare il carrello, ID azienda mancante');
        setSnackbar({
          open: true,
          message: 'Errore nel salvataggio del carrello. ID azienda mancante.',
          severity: 'error'
        });
        return;
      }
      
      // Assicurati che ogni elemento del carrello abbia l'ID azienda
      cart.forEach(item => {
        item.companyId = companyId;
      });
      
      console.log('🛒 Contenuto carrello:', cart);
      localStorage.setItem(`shopCart_${companyId}`, JSON.stringify(cart));
      
      setSnackbar({
        open: true,
        message: `Prodotto "${product.name}" aggiunto al carrello al prezzo di acquisto.`,
        severity: 'success'
      });
      
    } catch (err) {
      console.error('Errore nell\'aggiunta al carrello:', err);
      setSnackbar({
        open: true,
        message: 'Errore nell\'aggiunta al carrello. Riprova più tardi.',
        severity: 'error'
      });
      } finally {
        setLoading(false);
      }
    };

  const handleCloseSnackbar = () => {
    setSnackbar({...snackbar, open: false});
  };

  return (
    <Box sx={{ width: '100%', p: 0 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h4">
          Gestione Magazzino
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<WarningIcon />}
            onClick={() => setOpenLowStockDialog(true)}
            disabled={loading}
          >
            Prodotti in esaurimento
            {stats.lowStockCount > 0 && (
              <Chip 
                label={stats.lowStockCount} 
                color="error" 
                size="small" 
                sx={{ ml: 1 }}
              />
            )}
          </Button>
          
          <Button 
            variant="contained" 
            color="secondary"
            startIcon={<BarChartIcon />}
            onClick={() => setOpenStatsDialog(true)}
            disabled={loading}
          >
            Statistiche magazzino
          </Button>

          <WarehouseImportExport
            disabled={loading}
            onComplete={() => {
              setProductListRefreshKey((k) => k + 1);
              fetchInventoryStats();
            }}
          />
        </Box>
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <ProductList
          refreshKey={productListRefreshKey}
          onOpenManualLoad={() => setOpenLoadForm(true)}
          onOpenInventory={() => setOpenInventoryDialog(true)}
        />
      </Paper>

      <InventoryCountDialog
        open={openInventoryDialog}
        onClose={() => setOpenInventoryDialog(false)}
        onConfirmed={() => {
          setProductListRefreshKey((k) => k + 1);
          fetchInventoryStats();
        }}
      />

      {/* Dialog per il carico manuale */}
      <Dialog 
        open={openLoadForm} 
        onClose={() => setOpenLoadForm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Carico Manuale Magazzino</DialogTitle>
        <DialogContent>
          <WarehouseLoadForm />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLoadForm(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
      
      {/* Low Stock Dialog */}
      <Dialog 
        open={openLowStockDialog} 
        onClose={() => setOpenLowStockDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="error" sx={{ mr: 1 }} />
            <Typography variant="h6">Prodotti in esaurimento</Typography>
          </Box>
          <IconButton onClick={() => setOpenLowStockDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : stats.lowStockProducts.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                Nessun prodotto in esaurimento
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Tutti i prodotti hanno scorte superiori alla quantità minima
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Codice</TableCell>
                    <TableCell>Prodotto</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Scorta Attuale</TableCell>
                    <TableCell align="right">Scorta Minima</TableCell>
                    <TableCell align="center">Non riassortire / Carrello</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.lowStockProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>{product.code}</TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={product.stock} 
                          color="error" 
                          size="small" 
                          variant={product.stock === 0 ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell align="right">{product.minStock}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Segna come da non riassortire (non comparirà più in questo elenco)">
                          <IconButton 
                            color="secondary"
                            onClick={async () => {
                              try {
                                await api.put(`/products/${product.id}`, {
                                  ...product,
                                  doNotRestock: true
                                });
                                setSnackbar({
                                  open: true,
                                  message: `"${product.name}" segnato come da non riassortire`,
                                  severity: 'success'
                                });
                                await fetchInventoryStats();
                              } catch (err) {
                                setSnackbar({
                                  open: true,
                                  message: 'Errore nell\'aggiornamento',
                                  severity: 'error'
                                });
                              }
                            }}
                            size="small"
                          >
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Aggiungi al carrello">
                          <IconButton 
                            color="primary"
                            onClick={() => handleAddToCart(product)}
                            size="small"
                          >
                            <AddShoppingCartIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLowStockDialog(false)}>Chiudi</Button>
          <Button variant="contained" color="primary" onClick={fetchInventoryStats}>
            Aggiorna dati
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Stats Dialog */}
      <Dialog 
        open={openStatsDialog} 
        onClose={() => setOpenStatsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BarChartIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Statistiche magazzino</Typography>
          </Box>
          <IconButton onClick={() => setOpenStatsDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* Prima riga: KPI principali */}
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Valore totale (vendita)</Typography>
                    <Typography variant="h3" color="primary">
                      €{stats.totalValue.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Valore complessivo di tutti i prodotti in magazzino al prezzo di vendita
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Costo totale (acquisto)</Typography>
                    <Typography variant="h3" color="error">
                      €{stats.totalCost.toFixed(2)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Costo complessivo di tutti i prodotti in magazzino al prezzo di acquisto
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Margine potenziale</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h3" color="success.main">
                        €{stats.potentialProfit.toFixed(2)}
                      </Typography>
                      <ArrowUpwardIcon color="success" sx={{ ml: 1 }} />
                    </Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      Profitto potenziale totale su tutte le scorte
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Prodotti e categorie</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h3" color="secondary">
                          {stats.totalProducts}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Prodotti
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h3" color="info.main">
                          {stats.categories.length}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Categorie
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Seconda riga: Prezzi e grafici */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Prodotti più costosi (prezzo di vendita)
                    </Typography>
                    <BarChart 
                      data={stats.topSellingPrice}
                      title="Top 5 prodotti per prezzo di vendita"
                      valueKey="sellingPrice"
                      labelKey="name"
                      color="#2196f3"
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Prodotti più costosi (prezzo di acquisto)
                    </Typography>
                    <BarChart 
                      data={stats.topPurchasePrice}
                      title="Top 5 prodotti per prezzo di acquisto"
                      valueKey="purchasePrice"
                      labelKey="name"
                      color="#f44336"
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Prodotti con miglior margine
                    </Typography>
                    <BarChart 
                      data={stats.bestMargin}
                      title="Top 5 prodotti per margine di profitto (%)"
                      valueKey="margin"
                      labelKey="name" 
                      color="#4caf50"
                    />
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Nuova riga: Statistiche vendite e manutenzioni */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h5" gutterBottom>Statistiche Vendite e Manutenzioni</Typography>
        </Grid>
        
        <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
            <Typography variant="h6" gutterBottom>
                      Prodotti più venduti
            </Typography>
                    {stats.topSoldProducts && stats.topSoldProducts.length > 0 ? (
                      <BarChart 
                        data={stats.topSoldProducts}
                        title="Top 5 prodotti per numero di vendite"
                        valueKey="count"
                        labelKey="name"
                        color="#673ab7"
                      />
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                          Nessun dato sulle vendite disponibile
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          L'API delle statistiche di vendita non è ancora disponibile o non ha dati.
              </Typography>
                      </Box>
            )}
                  </CardContent>
                </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
            <Typography variant="h6" gutterBottom>
                      Prodotti più montati/installati
                    </Typography>
                    {stats.topMaintProducts && stats.topMaintProducts.length > 0 ? (
                      <BarChart 
                        data={stats.topMaintProducts}
                        title="Top 5 prodotti installati in manutenzioni"
                        valueKey="count"
                        labelKey="name"
                        color="#ff9800"
                      />
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
                          Nessun dato sulle manutenzioni disponibile
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          L'API delle statistiche di manutenzione non è ancora disponibile o non ha dati.
            </Typography>
              </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Terza riga: Tabella delle categorie */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Valore e profitto per categoria</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Categoria</TableCell>
                        <TableCell align="right">N° Prodotti</TableCell>
                        <TableCell align="right">Valore Vendita</TableCell>
                        <TableCell align="right">Costo Acquisto</TableCell>
                        <TableCell align="right">Profitto</TableCell>
                        <TableCell align="right">Margine %</TableCell>
                        <TableCell align="right">% del Magazzino</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stats.categories
                        .sort((a, b) => b.value - a.value)
                        .map((category) => (
                        <TableRow key={category.name}>
                          <TableCell>{category.name}</TableCell>
                          <TableCell align="right">{category.count}</TableCell>
                          <TableCell align="right">€{category.value.toFixed(2)}</TableCell>
                          <TableCell align="right">€{category.cost.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: category.profit > 0 ? 'success.main' : 'error.main' }}>
                            €{category.profit.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              {category.margin > 0 ? (
                                <TrendingUpIcon fontSize="small" color="success" sx={{ mr: 0.5 }} />
                              ) : (
                                <TrendingDownIcon fontSize="small" color="error" sx={{ mr: 0.5 }} />
                              )}
                              {category.margin.toFixed(1)}%
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {stats.totalValue > 0 
                              ? `${((category.value / stats.totalValue) * 100).toFixed(1)}%` 
                              : '0%'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
        </Grid>
      </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatsDialog(false)}>Chiudi</Button>
          <Button variant="contained" color="primary" onClick={fetchInventoryStats}>
            Aggiorna dati
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar per notifiche */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Inventory;